/**
 * AuthGate — the boot guard (genesis handoff §3.1). On mount: if there is already an
 * in-memory token, render the protected tree; otherwise attempt the silent refresh against
 * the HttpOnly cookie, and on failure stash the intended path and hand off to centralized
 * login.
 *
 * U3 mounts this around the app shell and the two feature routes; U2 builds and tests the
 * component itself.
 */
import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { AuthPending } from './AuthPending';
import { getAccessToken, getExpiresAt } from './authStore';
import { bootRefresh } from './silentRefresh';
import { startCentralizedLogin } from './loginKickoff';
import { useAuth } from './AuthContext';
import { useCopy } from '@/hooks/useCopy';

type GateState = 'booting' | 'authed' | 'redirecting';

export function AuthGate() {
  const location = useLocation();
  const { setAuthed, bootstrapPrincipal } = useAuth();
  const [state, setState] = useState<GateState>(() => (getAccessToken() ? 'authed' : 'booting'));
  const started = useRef(false);
  const { t } = useCopy();

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void (async () => {
      const existing = getAccessToken();
      if (existing) {
        setState('authed');
        void bootstrapPrincipal();
        return;
      }
      if (await bootRefresh()) {
        // The refresh already wrote token + expiry to the store; publish both through
        // context rather than re-deriving (or dropping) the expiry.
        const token = getAccessToken();
        if (token) setAuthed(token, getExpiresAt());
        void bootstrapPrincipal();
        setState('authed');
        return;
      }
      setState('redirecting');
      await startCentralizedLogin(location.pathname + location.search);
    })();
  }, [location.pathname, location.search, setAuthed, bootstrapPrincipal]);

  if (state === 'authed') return <Outlet />;
  return (
    <AuthPending
      message={
        state === 'redirecting'
          ? t('auth.redirecting', 'Redirecting to sign in…')
          : t('app.loading', 'Loading')
      }
    />
  );
}
